import { ELEVEN_LABS_API_KEY, ELEVEN_LABS_VOICE_ID } from 'config/app.config';

const TTS_ENDPOINT = 'https://api.elevenlabs.io/v1/text-to-speech';

let currentAudio = null;
let currentStopCallback = null;
let currentFinishPlayback = null;
let currentAudioKey = null;
let currentStartProgressUpdates = null;
let currentStopProgressUpdates = null;
let activeLessonScope = null;
const audioCache = new Map();
const pendingGenerations = new Map();
const queuedPrefetchKeys = new Set();
const prefetchQueue = [];
let prefetchRunning = false;

function revokeEntry(entry) {
  if (entry?.objectUrl) {
    URL.revokeObjectURL(entry.objectUrl);
  }
}

function getCacheKey(lessonScope, audioId) {
  return `${lessonScope || 'global'}::${audioId}`;
}

function setActiveLessonScope(lessonScope) {
  if (lessonScope) {
    activeLessonScope = lessonScope;
  }
}

function clearActivePlayback() {
  currentAudio = null;
  currentAudioKey = null;
  currentStopCallback = null;
  currentFinishPlayback = null;
  currentStartProgressUpdates = null;
  currentStopProgressUpdates = null;
}

function stopCurrentPlayback(triggerStop = true) {
  if (!currentAudio) return;

  const audio = currentAudio;
  const stopCallback = currentStopCallback;
  const finishPlayback = currentFinishPlayback;
  currentStopProgressUpdates?.();
  audio.onended = null;
  audio.onerror = null;
  audio.pause();
  audio.currentTime = 0;
  clearActivePlayback();
  if (triggerStop) {
    stopCallback?.();
  }
  finishPlayback?.();
}

function clearCacheEntries(predicate) {
  Array.from(audioCache.entries()).forEach(([key, entry]) => {
    if (!predicate(key, entry)) return;
    revokeEntry(entry);
    audioCache.delete(key);
  });
}

async function requestSpeechBlob(text) {
  if (!text) {
    throw new Error('Cannot generate speech without text');
  }
  if (!ELEVEN_LABS_API_KEY) {
    throw new Error('ELEVEN_LABS_API_KEY is not set');
  }
  if (!ELEVEN_LABS_VOICE_ID) {
    throw new Error('ELEVEN_LABS_VOICE_ID is not set');
  }

  const url = `${TTS_ENDPOINT}/${ELEVEN_LABS_VOICE_ID}`;

  const response = await fetch(
    url,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'xi-api-key': ELEVEN_LABS_API_KEY,
      },
      body: JSON.stringify({
        text,
        model_id: 'eleven_monolingual_v1',
        voice_settings: {
          stability: 0.5,
          similarity_boost: 0.5,
        },
      }),
    },
  );

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '(could not read body)');
    throw new Error(`ElevenLabs API error: ${response.status} ${errorBody}`);
  }

  return response.blob();
}

async function ensureAudioEntry({ lessonScope, audioId, text }) {
  setActiveLessonScope(lessonScope);
  const cacheKey = getCacheKey(lessonScope, audioId);
  const cachedEntry = audioCache.get(cacheKey);
  if (cachedEntry) {
    return cachedEntry;
  }

  const pending = pendingGenerations.get(cacheKey);
  if (pending) {
    return pending;
  }

  const generationPromise = requestSpeechBlob(text)
    .then(audioBlob => {
      const objectUrl = URL.createObjectURL(audioBlob);
      const entry = {
        audioId,
        text,
        objectUrl,
        lessonScope,
      };
      pendingGenerations.delete(cacheKey);
      if (
        lessonScope &&
        activeLessonScope &&
        lessonScope !== activeLessonScope
      ) {
        revokeEntry(entry);
        return entry;
      }
      audioCache.set(cacheKey, entry);
      return entry;
    })
    .catch(error => {
      pendingGenerations.delete(cacheKey);
      throw error;
    });

  pendingGenerations.set(cacheKey, generationPromise);
  return generationPromise;
}

async function drainPrefetchQueue() {
  if (prefetchRunning) return;
  prefetchRunning = true;

  while (prefetchQueue.length) {
    const nextTask = prefetchQueue.shift();
    if (!nextTask) continue;
    queuedPrefetchKeys.delete(nextTask.cacheKey);

    if (
      activeLessonScope &&
      nextTask.lessonScope &&
      nextTask.lessonScope !== activeLessonScope
    ) {
      continue;
    }

    try {
      await ensureAudioEntry(nextTask);
    } catch {
      // Prefetch failures should not interrupt active playback.
    }
  }

  prefetchRunning = false;
}

/**
 * Stop any in-flight TTS audio so voices don't overlap when the tutor
 * text changes rapidly (e.g. quick phase transitions).
 */
export function stopTutorSpeech() {
  stopCurrentPlayback(true);
}

export function pauseTutorSpeech() {
  if (!currentAudio) return false;
  currentStopProgressUpdates?.();
  currentAudio.pause();
  return true;
}

export async function resumeTutorSpeech() {
  if (!currentAudio) return false;
  await currentAudio.play();
  currentStartProgressUpdates?.();
  return true;
}

/**
 * Revoke any cached object URLs associated with the provided lesson scope.
 */
export function clearTutorAudioCache(lessonScope) {
  if (!lessonScope) {
    stopCurrentPlayback(false);
    clearCacheEntries(() => true);
    prefetchQueue.length = 0;
    queuedPrefetchKeys.clear();
    activeLessonScope = null;
    return;
  }

  if (
    currentAudio &&
    currentAudioKey &&
    currentAudioKey.startsWith(`${lessonScope}::`)
  ) {
    stopCurrentPlayback(false);
  }
  clearCacheEntries(key => key.startsWith(`${lessonScope}::`));
  for (let index = prefetchQueue.length - 1; index >= 0; index -= 1) {
    const queued = prefetchQueue[index];
    if (queued.lessonScope !== lessonScope) continue;
    queuedPrefetchKeys.delete(queued.cacheKey);
    prefetchQueue.splice(index, 1);
  }
  if (activeLessonScope === lessonScope) {
    activeLessonScope = null;
  }
}

/**
 * Ensure tutor audio is generated and cached.
 */
export async function ensureTutorAudio({
  lessonScope,
  audioId,
  text,
}) {
  return ensureAudioEntry({lessonScope, audioId, text});
}

/**
 * Queue future tutor audio clips for sequential prefetch.
 */
export function queueTutorAudioPrefetch({lessonScope, entries = []}) {
  setActiveLessonScope(lessonScope);

  entries
    .filter(entry => entry?.audioId && entry?.text)
    .forEach(entry => {
      const cacheKey = getCacheKey(lessonScope, entry.audioId);
      if (
        audioCache.has(cacheKey) ||
        pendingGenerations.has(cacheKey) ||
        queuedPrefetchKeys.has(cacheKey) ||
        currentAudioKey === cacheKey
      ) {
        return;
      }

      queuedPrefetchKeys.add(cacheKey);
      prefetchQueue.push({
        lessonScope,
        audioId: entry.audioId,
        text: entry.text,
        cacheKey,
      });
    });

  void drainPrefetchQueue();
}

/**
 * Play tutor audio from cache, generating and caching it on demand if needed.
 */
export async function playTutorAudio({
  lessonScope,
  audioId,
  text,
  onPlaybackStart,
  onPlaybackStop,
  onPlaybackProgress,
}) {
  stopCurrentPlayback(true);

  if (!text || !audioId) {
    return;
  }

  setActiveLessonScope(lessonScope);
  const entry = await ensureAudioEntry({ lessonScope, audioId, text });
  const cacheKey = getCacheKey(lessonScope, audioId);
  const audio = new Audio(entry.objectUrl);

  currentAudio = audio;
  currentAudioKey = cacheKey;
  currentStopCallback = onPlaybackStop || null;

  return new Promise((resolve, reject) => {
    let settled = false;
    let progressTimer = null;

    const getDurationMs = () =>
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration * 1000
        : null;

    const stopProgressUpdates = () => {
      if (progressTimer) {
        clearInterval(progressTimer);
        progressTimer = null;
      }
    };

    const emitProgress = () => {
      onPlaybackProgress?.({
        audioId,
        currentTimeMs: Math.max(0, audio.currentTime * 1000),
        durationMs: getDurationMs(),
      });
    };

    const startProgressUpdates = () => {
      emitProgress();
      stopProgressUpdates();
      progressTimer = setInterval(emitProgress, 120);
    };

    currentStartProgressUpdates = startProgressUpdates;
    currentStopProgressUpdates = stopProgressUpdates;

    const finalize = callback => {
      if (settled) return;
      settled = true;
      stopProgressUpdates();
      const shouldStop = currentAudio === audio;
      if (shouldStop) {
        clearActivePlayback();
      }
      callback?.();
      resolve();
    };

    currentFinishPlayback = () => {
      if (settled) return;
      settled = true;
      stopProgressUpdates();
      resolve();
    };

    audio.onended = () => {
      emitProgress();
      finalize(onPlaybackStop);
    };
    audio.onerror = err => {
      stopProgressUpdates();
      if (currentAudio === audio) {
        clearActivePlayback();
      }
      onPlaybackStop?.();
      reject(err);
    };

    audio.play()
      .then(() => {
        onPlaybackStart?.({
          audioId,
          durationMs: getDurationMs(),
        });
        startProgressUpdates();
      })
      .catch(err => {
        stopProgressUpdates();
        if (currentAudio === audio) {
          clearActivePlayback();
        }
        onPlaybackStop?.();
        reject(err);
      });
  });
}

/**
 * Backwards-compatible helper for text-only playback.
 */
export async function speakTutorText(text) {
  if (!text) {
    stopTutorSpeech();
    return;
  }

  return playTutorAudio({
    lessonScope: 'global',
    audioId: `text:${text}`,
    text,
  });
}
